/* eslint-disable @typescript-eslint/no-unused-expressions */
/**
 * Grows a buffer if it isn't large enough
 * @returns The original buffer if resized successfully, or a newly created buffer
 */
export function extendBuffer(buffer, newByteLength) {
    if (buffer.byteLength >= newByteLength)
        return buffer;
    if (ArrayBuffer.isView(buffer)) {
        const newBuffer = extendBuffer(buffer.buffer, newByteLength);
        return new buffer.constructor(newBuffer, buffer.byteOffset, newByteLength);
    }
    const isShared = typeof SharedArrayBuffer !== 'undefined' && buffer instanceof SharedArrayBuffer;
    // Note: If true, the buffer must be resizable/growable because of the first check.
    if (buffer.maxByteLength > newByteLength) {
        isShared ? buffer.grow(newByteLength) : buffer.resize(newByteLength);
        return buffer;
    }
    if (isShared) {
        const newBuffer = new SharedArrayBuffer(newByteLength);
        new Uint8Array(newBuffer).set(new Uint8Array(buffer));
        return newBuffer;
    }
    try {
        return buffer.transfer(newByteLength);
    }
    catch {
        const newBuffer = new ArrayBuffer(newByteLength);
        new Uint8Array(newBuffer).set(new Uint8Array(buffer));
        return newBuffer;
    }
}
export function toUint8Array(buffer) {
    if (buffer instanceof Uint8Array)
        return buffer;
    if (!ArrayBuffer.isView(buffer))
        return new Uint8Array(buffer);
    return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
}
/**
 * @hidden @deprecated
 */
export function initView(view, buffer, byteOffset, byteLength) {
    if (typeof buffer == 'number') {
        view.buffer = new ArrayBuffer(buffer);
        view.byteOffset = 0;
        view.byteLength = buffer;
        return;
    }
    if (!buffer
        || buffer instanceof ArrayBuffer
        || (globalThis.SharedArrayBuffer && buffer instanceof SharedArrayBuffer)) {
        const size = byteLength
            ?? view.constructor?.size // Memium types
            ?? buffer?.byteLength
            ?? 0;
        view.buffer = buffer ?? new ArrayBuffer(size);
        view.byteOffset = byteOffset ?? 0;
        view.byteLength = size;
        return;
    }
    if (ArrayBuffer.isView(buffer)) {
        view.buffer = buffer.buffer;
        view.byteOffset = buffer.byteOffset;
        view.byteLength = buffer.byteLength;
        return;
    }
    const u8 = new Uint8Array(buffer.length);
    view.buffer = u8.buffer;
    view.byteOffset = 0;
    view.byteLength = u8.length;
    for (let i = 0; i < u8.length; i++) {
        u8[i] = buffer[i];
    }
}
/** A generic view of an array buffer */
export class BufferView extends DataView {
    constructor(_buffer, _byteOffset, _byteLength) {
        const { buffer, byteOffset, byteLength } = new Uint8Array(_buffer, _byteOffset, _byteLength);
        super(buffer, byteOffset, byteLength);
    }
}
for (const key of Object.getOwnPropertyNames(DataView.prototype)) {
    if (!key.startsWith('get') && !key.startsWith('set'))
        continue;
    Object.defineProperty(BufferView.prototype, key, {
        value: () => {
            throw new ReferenceError('Do not use DataView methods on a BufferView.');
        },
        writable: false,
        enumerable: false,
        configurable: false,
    });
}
BufferView;
/** Creates a fixed-size array of a buffer view type */
export function BufferViewArray(element, size) {
    return class BufferViewArray extends Array {
        BYTES_PER_ELEMENT = size;
        constructor(_buffer, _byteOffset, _byteLength) {
            const { buffer, byteOffset, byteLength } = new Uint8Array(_buffer, _byteOffset, _byteLength);
            const length = (byteLength ?? size) / size;
            if (!Number.isSafeInteger(length))
                throw new Error('Invalid array length: ' + length);
            super(length);
            Object.assign(this, { buffer, byteOffset, byteLength });
            for (let i = 0; i < length; i++) {
                this[i] = new element(this.buffer, this.byteOffset + i * size, size);
            }
        }
    };
}
BufferView;
